import {
  LocationType,
  PlanStatus,
  ProductType,
  ResultType,
  UrlSubdirectoryTypes,
  getInventoryStatusCombination,
  searchParametersActions,
  selectMarketingStatus,
} from 'features/searchResultsFacets';
import MarketingStatus from 'enums/marketingStatus';
import { store } from '@redux/store';
import PillType from '../enums/popularPillsType';

const supportedLocationTypes = [
    LocationType.Market.toString(), 
    LocationType.City.toString(), 
    LocationType.SchoolDistrict.toString(), 
    LocationType.Neighborhood.toString(),
    LocationType.State.toString()];

/**
 * Dispatches an action to fetch consumer promos based on whether a pill is active or not.
 * @param isActive - A boolean value indicating whether the hot deals should be checked or not.
 */
const actionOnActiveHotDeals = (isActive) => searchParametersActions.fetchConsumerPromos(isActive);

const actionOnInactiveHotDeals = (isActive) => searchParametersActions.fetchConsumerPromos(isActive);

/**
 * Dispatches an action to fetch condos and Townhomes search parameters based on
 * whether a pill is active or not.
 * @param isActive - A boolean value indicating whether the Towhomes option is active or not.
 */
const updateCondosAndTownhomes = (isActive) => {
  const payload = {
    resultType: ResultType.Communities.toString(),
    productType: ProductType.Communities.toString(),
    condosAndTownHomes: isActive,
  };

  return searchParametersActions.fetchCondosAndTownHomes(payload);
} 

const actionOnActiveCondosAndTownhomes = () => updateCondosAndTownhomes(true);

const actionOnInactiveCondosAndTownhomes = () => updateCondosAndTownhomes(false);



/**
 * Dispatches an action to update the search parameters with the value of `isActive` for the "active adult" parameter.
 * @param isActive - A boolean value indicating whether the adult content should be included in the
 * search results or not.
 */
const actionOnActiveAdult = (isActive) => searchParametersActions.fetchActiveAdult(isActive);

/**
 * Dispatches an action to fetch the marketing status of "Coming Soon".
 */
const actionOnActiveComingSoon = () =>
  searchParametersActions.fetchMarketingStatus(MarketingStatus.ComingSoon.toString());

/**
 * Dispatches an action to fetch move-in dates for plans with the status "ReadyToBuild".
 */
const actionOnActiveFloorPlans = () =>
  searchParametersActions.fetchMoveInDateByStatus(PlanStatus.ReadyToBuild.toString());

/**
 * Dispatches an action to fetch the move-in date based on the inventory status combination.
 */
const actionOnActiveQuickMoveIns = () =>
  searchParametersActions.fetchMoveInDateByStatus(getInventoryStatusCombination());

/**
 * Dispatches an action to fetch any communities when the "Adult" pill is inactive.
 */
const actionOnInactiveAdult = () => searchParametersActions.fetchAnyCommunities();

/**
 * Dispatches an action to fetch marketing status when a pill is inactive.
 */
const actionOnInactiveComingSoon = () => {
  const state = store.getState();
  const currentMarketingStatus = selectMarketingStatus(state);
  const currentMarketingStatusList = currentMarketingStatus.split(',');

  // Filter out the 'Coming Soon' status
  const newMarketingStatus = currentMarketingStatusList.filter(
    status => status !== MarketingStatus.ComingSoon.toString()
  ).join(',');

  return searchParametersActions.fetchMarketingStatus(newMarketingStatus);
}

/**
 * Dispatches an action to clear the move-in date filter in the store.
 */
const actionOnInactiveFloorPlans = () => searchParametersActions.clearMoveInDateFilter();

/**
 * The function clears the move-in date filter when the "Quick Move-Ins" pill is inactive.
 */
const actionOnInactiveQuickMoveIns = () => searchParametersActions.clearMoveInDateFilter();

/**
 * This object is the source of truth for which pills should render when users are on a given subDirectory
 */
const pillsByUrlSubdirectory = {
  [UrlSubdirectoryTypes.Homes.toString()]: {
    [PillType.HotDeals.toString()]: {
      name: PillType.HotDeals.name,
      text: PillType.HotDeals.text,
      onActiveAction: actionOnActiveHotDeals,
      onInactiveAction: actionOnInactiveHotDeals,
    },
    [PillType.QuickMoveIns.toString()]: {
      name: PillType.QuickMoveIns.name,
      text: PillType.QuickMoveIns.text,
      onActiveAction: actionOnActiveQuickMoveIns,
      onInactiveAction: actionOnInactiveQuickMoveIns,
    },
    [PillType.FloorPlans.toString()]: {
      name: PillType.FloorPlans.name,
      text: PillType.FloorPlans.text,
      onActiveAction: actionOnActiveFloorPlans,
      onInactiveAction: actionOnInactiveFloorPlans,
    },
  },
  [UrlSubdirectoryTypes.Communities.toString()]: {
    [PillType.HotDeals.toString()]: {
      name: PillType.HotDeals.name,
      text: PillType.HotDeals.text,
      onActiveAction: actionOnActiveHotDeals,
      onInactiveAction: actionOnInactiveHotDeals,
    },
    [PillType.CondosAndTownhomes.toString()]: {
      name: PillType.CondosAndTownhomes.name,
      text: PillType.CondosAndTownhomes.text,
      onActiveAction: actionOnActiveCondosAndTownhomes,
      onInactiveAction: actionOnInactiveCondosAndTownhomes,
    },
    [PillType.Adult.toString()]: {
      name: PillType.Adult.name,
      text: PillType.Adult.text,
      onActiveAction: actionOnActiveAdult,
      onInactiveAction: actionOnInactiveAdult,
    },
    [PillType.ComingSoon.toString()]: {
      name: PillType.ComingSoon.name,
      text: PillType.ComingSoon.text,
      onActiveAction: actionOnActiveComingSoon,
      onInactiveAction: actionOnInactiveComingSoon,
    },
  },
  [UrlSubdirectoryTypes.CondosAndTownHomes.toString()]: {
    [PillType.HotDeals.toString()]: {
      name: PillType.HotDeals.name,
      text: PillType.HotDeals.text,
      onActiveAction: actionOnActiveHotDeals,
      onInactiveAction: actionOnInactiveHotDeals,
    },
    [PillType.QuickMoveIns.toString()]: {
      name: PillType.QuickMoveIns.name,
      text: PillType.QuickMoveIns.text,
      onActiveAction: actionOnActiveQuickMoveIns,
      onInactiveAction: actionOnInactiveQuickMoveIns,
    },
    [PillType.FloorPlans.toString()]: {
      name: PillType.FloorPlans.name,
      text: PillType.FloorPlans.text,
      onActiveAction: actionOnActiveFloorPlans,
      onInactiveAction: actionOnInactiveFloorPlans,
    },
  },
  [UrlSubdirectoryTypes.FloorPlans.toString()]: {
    [PillType.HotDeals.toString()]: {
      name: PillType.HotDeals.name,
      text: PillType.HotDeals.text,
      onActiveAction: actionOnActiveHotDeals,
      onInactiveAction: actionOnInactiveHotDeals,
    },
    [PillType.QuickMoveIns.toString()]: {
      name: PillType.QuickMoveIns.name,
      text: PillType.QuickMoveIns.text,
      onActiveAction: actionOnActiveQuickMoveIns,
      onInactiveAction: actionOnInactiveQuickMoveIns,
    },
    [PillType.FloorPlans.toString()]: {
      name: PillType.FloorPlans.name,
      text: PillType.FloorPlans.text,
      onActiveAction: actionOnActiveFloorPlans,
      onInactiveAction: actionOnInactiveFloorPlans,
    },
  },
  [UrlSubdirectoryTypes.CondoDevelopment.toString()]: {
    [PillType.HotDeals.toString()]: {
      name: PillType.HotDeals.name,
      text: PillType.HotDeals.text,
      onActiveAction: actionOnActiveHotDeals,
      onInactiveAction: actionOnInactiveHotDeals,
    },
    [PillType.CondosAndTownhomes.toString()]: {
      name: PillType.CondosAndTownhomes.name,
      text: PillType.CondosAndTownhomes.text,
      onActiveAction: actionOnActiveCondosAndTownhomes,
      onInactiveAction: actionOnInactiveCondosAndTownhomes,
    },
    [PillType.Adult.toString()]: {
      name: PillType.Adult.name,
      text: PillType.Adult.text,
      onActiveAction: actionOnActiveAdult,
      onInactiveAction: actionOnInactiveAdult,
    },
    [PillType.ComingSoon.toString()]: {
      name: PillType.ComingSoon.name,
      text: PillType.ComingSoon.text,
      onActiveAction: actionOnActiveComingSoon,
      onInactiveAction: actionOnInactiveComingSoon,
    },
  },
};

/**
 * Returns the pills that should render.
 * @param urlSubdirectory The url subdirectory that we want to render pills on.
 */
export function getPopularSearchesPillsForUrlSubdirectory(urlSubdirectory) {
  const popularPillsBySubdirectory = pillsByUrlSubdirectory[urlSubdirectory];
  const pillsByUrlToRender = { ...popularPillsBySubdirectory }

  return pillsByUrlToRender;
}

/**
 * The function `getPillsByCounts` takes a URL subdirectory and an array of pill types with counts, and
 * returns an array of pill details for the given URL subdirectory. Also, the pill could be set as
 * disabled if it does not have results to display, it is defined in `pillsWithCounts`. 
 * @param urlSubdirectory - The `urlSubdirectory` parameter is a string that represents the
 * subdirectory of a URL. It is used to retrieve the popular searches pills for that specific
 * subdirectory.
 * @param pillsWithCounts - An array with the pills types that contians counts.
 * @returns an array of pill details based on the given URL subdirectory and pill types with counts.
 */
export function getPillsByCounts(urlSubdirectory, pillsWithCounts, locationType) {
  const pillsDetails = getPopularSearchesPillsForUrlSubdirectory(urlSubdirectory);
  if (!pillsDetails || !supportedLocationTypes.includes(locationType)) {
    return [];
  }

  // if the Pill is not presented in the `pillsWithCounts` when we perform a comparison
  // it will be disabled since it means that the pill does not have results to display. 
  // Finally, by business rule the disabled pills will go at the end of the list.
  const pillsList = Object.values(pillsDetails).map((pill) => {
    pill.isEnabled = pillsWithCounts.includes(pill.name);
    return pill;
  }).sort((secondPill, firstPill) => firstPill.isEnabled - secondPill.isEnabled);

  return pillsList;
}

/**
 * The function `getPillAction` returns the action associated with a pill based on the URL
 * subdirectory, pill type, and whether it is active or inactive.
 * @param urlSubdirectory - The `urlSubdirectory` parameter is a string that represents the
 * subdirectory of a URL.
 * @param pillType - The `pillType` parameter represents the type of pill. It is used to access the
 * corresponding pill object from the `pills` array.
 * @param isActive - A boolean value indicating whether the pill is active or not.
 * @returns the action associated with the specified pill type and whether it is active or inactive.
 */
export function getPillAction(urlSubdirectory, pillType, isActive) {
  const pills = pillsByUrlSubdirectory[urlSubdirectory];
  const pill = pills?.[pillType];

  if (!pill) {
    return null;
  }

  const action = isActive ? pill.onActiveAction : pill.onInactiveAction;

  return action;
}
